import { call, put, takeEvery, all, fork, select } from 'redux-saga/effects'
import { resetLoading, showLoading } from 'react-redux-loading-bar'

import * as actions from './actions'
import { expireSession } from '../auth/actions'
import { fetch } from 'api'
import { camelizeKeys } from 'humps'

const abortControllers = new Map<string, AbortController>()

export function* sendRequest(action: any): Generator<any, any, any> {
  const { method, url, cancelKey, successAction, failureAction, body, redirect, notification, endpointType, skipGlobalErrorHandling, context } = action

  let controller: AbortController | undefined
  if (cancelKey) {
    abortControllers.get(cancelKey)?.abort()
    controller = new AbortController()
    abortControllers.set(cancelKey, controller)
  }

  try {
    if (!action.hideLoading) {
      const isLoadingBarShowing = yield select(state =>
        state.loadingBar.default ? state.loadingBar.default !== 0 : false)
      if (!isLoadingBarShowing) {
        yield put(showLoading())
      }
    }

    const options = { body, method, signal: controller?.signal } as const
    const response = yield call(fetch, url, options)
    const { ok, body: result } = response

    if (!ok || result.errors) {
      yield put({ type: actions.REQUEST_FAIL, failureAction, url, response, skipGlobalErrorHandling, context })
    } else {
      yield put({ type: successAction, ...camelizeKeys(result), redirect, notification, endpointType, context })
    }
  } catch (e: any) {
    if (e?.name === 'AbortError') return
    yield put({ type: failureAction, errors: [camelizeKeys(e)], context })
    if (!skipGlobalErrorHandling) {
      yield put({ type: actions.ADD_REQUEST_ERROR, error: { url, status: 'failed_to_fetch' } })
    }
  } finally {
    if (cancelKey && abortControllers.get(cancelKey) === controller) {
      abortControllers.delete(cancelKey)
    }
    yield put({ type: actions.REQUEST_DONE })
  }
}

function* requestDone(action: any): Generator<any, any, any> {
  const inFlightRequestsNum = yield select(state => state.request.inFlight)

  if (inFlightRequestsNum < 1) {
    yield put(resetLoading())
  }
}

export function tokenInvalidErrorPresent(status: any, errors: Array<any>): boolean {
  return status.toString() === '401' && errors.some((e) => e.source === 'token')
}

export function* requestFail(action: any): Generator<any, any, any> {
  const {
    url,
    failureAction,
    skipGlobalErrorHandling,
    context,
    response: { status, statusText, body }
  } = action

  const errors = camelizeKeys(Array.isArray(body?.errors) ? body.errors : [])

// @ts-expect-error - TS2345 - Argument of type 'Camelized<any>' is not assignable to parameter of type 'any[]'.
  if (tokenInvalidErrorPresent(status, errors)) {
    yield put(expireSession())
  } else {
    yield put({ type: failureAction, status, errors, context })
    if (!skipGlobalErrorHandling) {
      yield put({ type: actions.ADD_REQUEST_ERROR, error: { url, status, statusText, errors } })
    }
  }
}

function* watchAuthRequests(): Generator<any, any, any> {
  yield takeEvery(actions.AUTHED_REQUEST, sendRequest)
}

function* watchUnauthRequests(): Generator<any, any, any> {
  yield takeEvery(actions.UNAUTHED_REQUEST, sendRequest)
}

function* watchRequestDone(): Generator<any, any, any> {
  yield takeEvery(actions.REQUEST_DONE, requestDone)
}

function* watchRequestFail(): Generator<any, any, any> {
  yield takeEvery(actions.REQUEST_FAIL, requestFail)
}

export default function* requestSagas(): Generator<any, any, any> {
  yield all([
    fork(watchAuthRequests),
    fork(watchUnauthRequests),
    fork(watchRequestDone),
    fork(watchRequestFail)
  ])
}
